import React, { FC } from 'react';
import { Button, TableRow, TableCell } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FeedKey, UserFeedFollowGroupFragment } from 'api';
import { FollowToggle, PrivateGroupIcon, TableCellWithOverflow } from 'components';
import { useCurrentUser } from 'hooks';
import { isGroupOwnerOrModerator } from 'features/groups/helpers';

import ImageCell from '../GroupCells/Image';
import TitleCell from '../GroupCells/Title';
import MembersCell from '../GroupCells/Members';

interface IProps {
  group: UserFeedFollowGroupFragment;
}

const useStyles = makeStyles(theme => ({
  row: {
    '&:nth-child(even)': {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  icon: {
    padding: `0 ${theme.spacing()}px`,
  },
  actionButton: {
    width: theme.spacing(10),
  },
  greyButton: {
    color: theme.palette.grey[600],
  },
}));

const ClientRow: FC<IProps> = ({ group }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const currentUser = useCurrentUser();
  const canEdit = isGroupOwnerOrModerator(currentUser, group.id);

  const buttonClasses = (isFollowing: boolean) =>
    classNames(classes.actionButton, { [classes.greyButton]: isFollowing });

  function renderActionButton() {
    if (canEdit) {
      return (
        <Button
          variant="outlined"
          classes={{ root: classes.actionButton }}
          onClick={() => history.push(`/group/${group.id}/edit`)}
        >
          {t('common:edit')}
        </Button>
      );
    }

    return (
      <FollowToggle source={group} type={FeedKey.Group}>
        {({ isFollowing, loading, onClick }) => (
          <Button
            color={isFollowing ? 'default' : 'primary'}
            variant="outlined"
            classes={{ root: buttonClasses(isFollowing) }}
            onClick={onClick}
            disabled={loading}
          >
            {isFollowing ? t('common:leave') : t('common:join')}
          </Button>
        )}
      </FollowToggle>
    );
  }

  return (
    <TableRow classes={{ root: classes.row }} key={group.id}>
      <TableCell padding="none">
        <ImageCell group={group} />
      </TableCell>
      <TableCell className={classes.icon} align="center">
        <PrivateGroupIcon group={group} type="index" />
      </TableCell>
      <TableCellWithOverflow padding="none">
        <TitleCell group={group} />
      </TableCellWithOverflow>
      <TableCell align="center">
        <MembersCell group={group} />
      </TableCell>
      <TableCell>{renderActionButton()}</TableCell>
    </TableRow>
  );
};

export default ClientRow;
